/**
 * Helpers for DateTimePicker (parse, format, weekdays).
 * For Media Devoted | Created By Ciro Autiero
 */

import { format, isValid } from 'date-fns';

const DANISH_TZ = 'Europe/Copenhagen';

/**
 * Parse "YYYY-MM-DDTHH:mm" or ISO string.
 * Uses regex for datetime-local to avoid browser inconsistency (some treat no-Z as UTC).
 */
export function parseDateTimePickerValue(v: string): Date | null {
  if (!v?.trim()) return null;
  const m = v.trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{1,2}):(\d{2})/);
  if (m) {
    const [, y, mo, d, h, mi] = m.map(Number);
    return new Date(y, mo - 1, d, h, mi || 0, 0);
  }
  const d = new Date(v);
  return isValid(d) ? d : null;
}

export function toDateTimeLocal(d: Date): string {
  return format(d, "yyyy-MM-dd'T'HH:mm");
}

export function toTimeString(d: Date): string {
  return format(d, 'HH:mm');
}

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Current Danish datetime as "YYYY-MM-DDTHH:mm" (Europe/Copenhagen). */
export function danishNowLocal(): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: DANISH_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const parts = fmt.formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/** Current time in Europe/Copenhagen formatted as HH:mm. */
export function currentDanishTime(): string {
  return danishNowLocal().slice(11);
}

/** Clamp a datetime-local string so it is never before Danish "now". */
export function clampPastDanish(dtLocal: string): { value: string; timeStr: string } {
  const nowLocal = danishNowLocal();
  if (dtLocal >= nowLocal) return { value: dtLocal, timeStr: dtLocal.slice(11) };
  return { value: nowLocal, timeStr: nowLocal.slice(11) };
}
